import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Avatar,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Email,
  ExitToApp,
  Security,
  DeleteForever,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useWorkout } from '../hooks/useWorkout';

export const UserProfile: React.FC = () => {
  const { user, signOut, loading } = useAuth();
  const { resetProgress } = useWorkout();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const handleSignOut = async () => {
    setError(null);
    const { error } = await signOut();
    if (error) {
      setError(error.message);
    }
    setLogoutDialogOpen(false);
  };

  const handleResetData = async () => {
    setError(null);
    setIsResetting(true);
    try {
      await resetProgress();
      setResetDialogOpen(false);
      // Show success message
      setError(null);
    } catch (err) {
      setError('Failed to reset data. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Profile
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your account settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Profile Card */}
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mr: 3,
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
            }}
          >
            {user.user_metadata?.full_name 
              ? getInitials(user.user_metadata.full_name)
              : <Person sx={{ fontSize: 40 }} />
            }
          </Avatar>
          <Box>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              {user.user_metadata?.full_name || 'User'}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Welcome back to your fitness journey!
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* User Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Account Information
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Person sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Full Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {user.user_metadata?.full_name || 'Not provided'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Email sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Email Address
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {user.email}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Security sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Account Created
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(user.created_at)}
              </Typography>
            </Box>
          </Box>

          {user.email_confirmed_at && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Security sx={{ mr: 2, color: 'success.main' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email Verified
                </Typography>
                <Typography variant="body1" fontWeight="medium" color="success.main">
                  ✓ Verified on {formatDate(user.email_confirmed_at)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<DeleteForever />}
            onClick={() => setResetDialogOpen(true)}
            sx={{
              fontWeight: 'bold',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              },
            }}
          >
            Reset All Data
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ExitToApp />}
            onClick={() => setLogoutDialogOpen(true)}
            sx={{
              fontWeight: 'bold',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              },
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Paper>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="h2" fontWeight="bold">
            Sign Out
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to sign out? You'll need to sign in again to access your workout data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSignOut}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <ExitToApp />}
          >
            {loading ? 'Signing Out...' : 'Sign Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Data Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="h2" fontWeight="bold" color="warning.main">
            Reset All Data
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ⚠️ <strong>This action cannot be undone!</strong>
          </Typography>
          <Typography variant="body1">
            This will permanently delete all of your workout data including:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2 }}>
            <li>All workout history and progress</li>
            <li>Personal records</li>
            <li>Current workout state</li>
            <li>Week and day progress</li>
          </Box>
          <Typography variant="body1">
            You will start fresh from Week 1, Day 1. Are you absolutely sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetData}
            variant="contained"
            color="warning"
            disabled={isResetting}
            startIcon={isResetting ? <CircularProgress size={16} /> : <DeleteForever />}
          >
            {isResetting ? 'Resetting...' : 'Reset All Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}; 